import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { FaSync } from 'react-icons/fa';
import api from '../../services/api';
import { defaultSelectStyles } from '../../utils/selectStyles';
import InfoTip from '../../components/InfoTip';
import { BROWN, fmtDateFr } from './components/commun';
import { SanteBanner } from './components/Indicateurs';
import ListeModal from './components/ListeModal';
import { VueEnsemble, MainOeuvre, Materiel, Entretien, Citernes } from './Onglets';

// Dashboard technique v2 : bandeau santé + 5 onglets chargés à la demande. Chaque indicateur ouvre une liste,
// chaque ligne de liste ouvre la fiche de l'entité. Backend : controllers/dashboard/ (/dashboard/v2/*).

const LS_KEY = 'dashboard_v2_reglages';
const ONGLETS = [
  { id: 'ensemble',    label: "Vue d'ensemble",     endpoint: 'overview',    Composant: VueEnsemble },
  { id: 'mainOeuvre',  label: "Main-d'œuvre",       endpoint: 'main-oeuvre', Composant: MainOeuvre },
  { id: 'materiel',    label: 'Matériel & gasoil',  endpoint: 'materiel',    Composant: Materiel },
  { id: 'entretien',   label: 'Entretien',          endpoint: 'entretien',   Composant: Entretien },
  { id: 'citernes',    label: 'Citernes',           endpoint: 'citernes',    Composant: Citernes },
];

const pad2 = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const hier = () => { const d = new Date(); d.setDate(d.getDate() - 1); return iso(d); };
// Cycle de paie (21 → 20) contenant la date
const cycleDe = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  const debut = d >= 21 ? new Date(y, m - 1, 21) : new Date(y, m - 2, 21);
  return { start: iso(debut), end: iso(new Date(debut.getFullYear(), debut.getMonth() + 1, 20)) };
};
// Périodes prédéfinies (bornées à hier : la journée en cours est encore en saisie)
const PERIODES = {
  cycle:     { label: 'Cycle de paie en cours', calc: () => { const c = cycleDe(hier()); return { start: c.start, end: c.end > hier() ? hier() : c.end }; } },
  cyclePrec: { label: 'Cycle précédent', calc: () => { const c = cycleDe(hier()); const [y, m] = c.start.split('-').map(Number); return cycleDe(iso(new Date(y, m - 1, 20))); } },
  mois:      { label: 'Mois en cours', calc: () => { const h = hier(); return { start: `${h.slice(0, 8)}01`, end: h }; } },
  perso:     { label: 'Personnalisée', calc: null },
};

const loadReglages = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null') || {}; } catch { return {}; } };

const DashboardTechniquePage = () => {
  const saved = useMemo(loadReglages, []);
  const [periodeType, setPeriodeType] = useState(saved.periodeType && PERIODES[saved.periodeType] ? saved.periodeType : 'cycle');
  const [dates, setDates] = useState(() => (periodeType === 'perso' && saved.dates ? saved.dates : (PERIODES[periodeType].calc || PERIODES.cycle.calc)()));
  const [societe, setSociete] = useState(saved.societe || null);
  const [chantierIds, setChantierIds] = useState(saved.chantierIds || []);
  const [comparer, setComparer] = useState(saved.comparer ?? true);
  const [onglet, setOnglet] = useState(saved.onglet || 'ensemble');

  const [chantiers, setChantiers] = useState([]);
  const [sante, setSante] = useState(null);
  const [donnees, setDonnees] = useState({}); // { [onglet]: { cle, data } }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [liste, setListe] = useState(null);
  const [rafraichir, setRafraichir] = useState(0);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify({ periodeType, dates, societe, chantierIds, comparer, onglet })); } catch { /* stockage indisponible */ }
  }, [periodeType, dates, societe, chantierIds, comparer, onglet]);

  useEffect(() => { api.get('/chantiers').then(r => setChantiers(r.data || [])).catch(() => {}); }, []);

  // Paramètres envoyés à toutes les routes /dashboard/v2/*
  const params = useMemo(() => ({
    start: dates.start, end: dates.end,
    societe: societe || undefined,
    chantierIds: chantierIds.length ? chantierIds.join(',') : undefined,
  }), [dates, societe, chantierIds]);
  const cle = JSON.stringify(params);

  // Santé (bandeau) : rechargée à chaque changement de filtres
  useEffect(() => {
    setSante(null);
    api.get('/dashboard/v2/sante', { params }).then(r => setSante(r.data)).catch(() => setError('Erreur lors du chargement de la santé'));
  }, [params, rafraichir]);

  // Onglet courant : chargé à la demande, mis en cache tant que les filtres ne changent pas
  useEffect(() => {
    const o = ONGLETS.find(x => x.id === onglet);
    if (donnees[onglet]?.cle === cle) return;
    setLoading(true);
    setError('');
    api.get(`/dashboard/v2/${o.endpoint}`, { params })
      .then(r => setDonnees(d => ({ ...d, [onglet]: { cle, data: r.data } })))
      .catch(e => setError(e?.response?.data?.message || 'Erreur lors du chargement'))
      .finally(() => setLoading(false));
  }, [onglet, cle, params, rafraichir]); // eslint-disable-line react-hooks/exhaustive-deps

  const actualiser = () => { setDonnees({}); setRafraichir(n => n + 1); };
  const ouvrirListe = useCallback((kind, titre, query) => setListe({ kind, titre, query }), []);

  const choisirPeriode = (type) => {
    setPeriodeType(type);
    if (PERIODES[type].calc) setDates(PERIODES[type].calc());
  };

  const societeOptions = [...new Set(chantiers.map(c => c.societe || 'Non renseignée'))].sort().map(s => ({ value: s, label: s }));
  const chantierOptions = chantiers.filter(c => !societe || (c.societe || 'Non renseignée') === societe).map(c => ({ value: c.id, label: c.name }));
  const courant = ONGLETS.find(x => x.id === onglet);
  const data = donnees[onglet]?.cle === cle ? donnees[onglet].data : null;
  const periode = { start: dates.start, end: dates.end };

  return (
    <div className="container-fluid px-3 px-md-4 pb-5">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 my-3">
        <h4 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>
          🛠 Dashboard technique <InfoTip text="Cliquez sur un indicateur pour voir la liste, puis sur un nom pour ouvrir sa fiche. Les filtres s'appliquent à tous les chiffres." />
        </h4>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={actualiser} disabled={loading}><FaSync /> Actualiser</button>
        </div>
      </div>

      {/* Filtres */}
      <div className="p-3 mb-3" style={{ background: '#f9f6ef', borderRadius: 10, border: '1px solid #e8dcc8' }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1">Période</label>
            <select className="form-select form-select-sm" value={periodeType} onChange={e => choisirPeriode(e.target.value)}>
              {Object.entries(PERIODES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Du</label>
            <input type="date" className="form-control form-control-sm" value={dates.start}
              onChange={e => { if (e.target.value) { setPeriodeType('perso'); setDates(d => ({ ...d, start: e.target.value })); } }} />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label small mb-1">Au</label>
            <input type="date" className="form-control form-control-sm" value={dates.end}
              onChange={e => { if (e.target.value) { setPeriodeType('perso'); setDates(d => ({ ...d, end: e.target.value })); } }} />
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label small mb-1">Société</label>
            <Select isClearable styles={defaultSelectStyles} placeholder="Toutes" options={societeOptions}
              value={societeOptions.find(o => o.value === societe) || null}
              onChange={o => { setSociete(o?.value || null); setChantierIds([]); }} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small mb-1">Chantiers</label>
            <Select isMulti styles={defaultSelectStyles} placeholder="Tous" options={chantierOptions}
              value={chantierOptions.filter(o => chantierIds.includes(o.value))}
              onChange={opts => setChantierIds((opts || []).map(o => o.value))} />
          </div>
        </div>
        <label className="d-flex align-items-center gap-2 small mt-2" style={{ cursor: 'pointer', width: 'fit-content' }}>
          <input type="checkbox" checked={comparer} onChange={e => setComparer(e.target.checked)} />
          Comparer à la période précédente
        </label>
      </div>

      {error && <Alert variant="danger" className="py-2" onClose={() => setError('')} dismissible>{error}</Alert>}

      {sante ? <SanteBanner sante={sante} onListe={ouvrirListe} />
        : <div className="small text-muted mb-3"><Spinner animation="border" size="sm" /> Calcul de la santé…</div>}

      {/* Onglets */}
      <ul className="nav nav-tabs mb-3" role="tablist">
        {ONGLETS.map(o => (
          <li className="nav-item" key={o.id}>
            <button type="button" role="tab" aria-selected={onglet === o.id}
              className={`nav-link ${onglet === o.id ? 'active fw-bold' : ''}`} style={{ color: BROWN }}
              onClick={() => setOnglet(o.id)}>
              {o.label}
            </button>
          </li>
        ))}
        <li className="ms-auto small text-muted align-self-center">{fmtDateFr(dates.start)} → {fmtDateFr(dates.end)}</li>
      </ul>

      {loading && !data && <div className="text-center py-5"><Spinner animation="border" size="sm" /> Chargement…</div>}
      {data && <div style={{ opacity: loading ? 0.5 : 1 }}><courant.Composant data={data} onListe={ouvrirListe} periode={periode} comparer={comparer} /></div>}

      <ListeModal liste={liste} params={params} onClose={() => setListe(null)} />
    </div>
  );
};

export default DashboardTechniquePage;
